import type { Activity, ItineraryDay, Venue } from '../types';

/**
 * The stops a saved place has in the plan, and how to take them out of the page
 * state once the server has taken them out of the trip.
 *
 * Deleting a place deletes its stops (see `venueRemoval.service.ts`). Both tabs
 * that can delete a place read this: the warning before, and the cleanup after,
 * have to agree on what "its stops" means — by `venueId`, never by name.
 */

/**
 * Day numbers holding a stop of this place, ascending and deduped.
 *
 * The origin card (`isOrigin`) is not a stop: it is the server's copy of the
 * previous day's last stop, so a day that starts where the last one ended does
 * not count as another day at that place. The same place planned on two days
 * for real still counts twice. Mirrors `activitiesForVenue`, which the
 * add-to-itinerary modal ticks from.
 */
export function dayNumbersOfVenue(days: ItineraryDay[], venueId: string): number[] {
  return days
    .filter(d => d.activities.some(a => !a.isOrigin && a.venueId === venueId))
    .map(d => d.dayNumber)
    .sort((a, b) => a - b);
}

function nameIndex(venues: Venue[]): Map<string, string> {
  return new Map(venues.map(v => [v.name.toLowerCase(), v.id]));
}

/** The place a stop goes to: its venueId, or a place of the same name. */
function venueIdOfStop(activity: Activity, nameToId: Map<string, string>): string | null {
  return activity.venueId
    ?? (activity.venueName ? nameToId.get(activity.venueName.toLowerCase()) : undefined)
    ?? nameToId.get(activity.title.toLowerCase())
    ?? null;
}

/** venueId → day numbers of its stops, for every place (matches by name when a stop has no venueId). */
export function dayNumbersByVenue(days: ItineraryDay[], venues: Venue[]): Map<string, number[]> {
  const nameToId = nameIndex(venues);
  const map = new Map<string, number[]>();
  for (const day of days) {
    for (const activity of day.activities) {
      if (activity.isOrigin) continue;
      const vid = venueIdOfStop(activity, nameToId);
      if (!vid) continue;
      const existing = map.get(vid) ?? [];
      if (!existing.includes(day.dayNumber)) map.set(vid, [...existing, day.dayNumber]);
    }
  }
  return map;
}

/** Places under one day of the plan; `dayNumber: null` holds the ones on no day. */
export interface VenueDaySection {
  dayNumber: number | null;
  venues: Venue[];
}

/**
 * The Places list in the order of the trip: a place sits under the first day
 * it is planned on, in its order within that day, and places on no day come
 * last in the order they had. A place on two days shows once — its card lists
 * every day.
 */
export function venueSectionsByDay(days: ItineraryDay[], venues: Venue[]): VenueDaySection[] {
  const nameToId = nameIndex(venues);
  const first = new Map<string, { dayNumber: number; sortOrder: number }>();
  for (const day of days) {
    for (const activity of day.activities) {
      if (activity.isOrigin) continue;
      const vid = venueIdOfStop(activity, nameToId);
      if (!vid) continue;
      const prev = first.get(vid);
      const earlier = !prev
        || day.dayNumber < prev.dayNumber
        || (day.dayNumber === prev.dayNumber && activity.sortOrder < prev.sortOrder);
      if (earlier) first.set(vid, { dayNumber: day.dayNumber, sortOrder: activity.sortOrder });
    }
  }

  const byDay = new Map<number, Array<{ venue: Venue; sortOrder: number }>>();
  const unscheduled: Venue[] = [];
  for (const venue of venues) {
    const at = first.get(venue.id);
    if (!at) { unscheduled.push(venue); continue; }
    byDay.set(at.dayNumber, [...(byDay.get(at.dayNumber) ?? []), { venue, sortOrder: at.sortOrder }]);
  }

  const sections: VenueDaySection[] = [...byDay.entries()]
    .sort(([a], [b]) => a - b)
    .map(([dayNumber, list]) => ({
      dayNumber,
      venues: [...list].sort((x, y) => x.sortOrder - y.sortOrder).map(e => e.venue),
    }));
  if (unscheduled.length > 0) sections.push({ dayNumber: null, venues: unscheduled });
  return sections;
}

/** The itinerary without that place's stops — what the server just did. */
export function dropVenueStops(days: ItineraryDay[], venueId: string): ItineraryDay[] {
  return days.map(d => (
    d.activities.some(a => a.venueId === venueId)
      ? { ...d, activities: d.activities.filter(a => a.venueId !== venueId) }
      : d
  ));
}
